/**
 * Composant réutilisable pour la sélection de raisons d'annulation
 * Support suggestions contextuelles + saisie libre + validation temps réel
 */

import React, { useEffect } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  Chip,
  Stack,
  Alert,
  ListSubheader
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import { useCancellationReasons } from '../hooks/useCancellationReasons';
import {
  ErrorOutline as ErrorIcon,
  CheckCircle as CheckIcon,
  Category as CategoryIcon
} from '@mui/icons-material';

const CancellationReasonSelector = ({
  context,
  userRole,
  value,
  onChange,
  onValidationChange,
  required = true,
  disabled = false,
  showAnalytics = false,
  variant = 'outlined'
}) => {
  const { t } = useTranslation(['cancellationReasons', 'common']);
  const theme = useTheme();
  
  const {
    suggestedReasons,
    selectedReason,
    customReason,
    errors,
    isValid,
    setSelectedReason,
    setCustomReason,
    getFinalReason,
    getAnalyticsData
  } = useCancellationReasons(context, userRole);

  // 🔄 AMÉLIORATION: Notifier les changements SANS useEffect pour éviter boucles
  React.useEffect(() => {
    if (onChange) {
      const data = {
        reason: getFinalReason(),
        analytics: getAnalyticsData()
      };
      onChange(data);
    }
  }, [selectedReason, customReason, onChange, getFinalReason, getAnalyticsData]);

  // 🔄 AMÉLIORATION: Notifier les changements de validation
  React.useEffect(() => {
    if (onValidationChange) {
      onValidationChange(isValid);
    }
  }, [isValid, onValidationChange]);

  // Grouper les raisons par catégorie pour l'affichage
  const reasonsByCategory = React.useMemo(() => {
    const grouped = {};
    suggestedReasons.forEach(reason => {
      if (!grouped[reason.category]) {
        grouped[reason.category] = [];
      }
      grouped[reason.category].push(reason);
    });
    return grouped;
  }, [suggestedReasons]);

  // Obtenir la couleur selon la catégorie
  const getCategoryColor = (category) => {
    const colors = {
      equipment: theme.palette.warning.main,
      weather: theme.palette.info.main,
      personal: theme.palette.success.main,
      safety: theme.palette.error.main,
      communication: theme.palette.primary.main,
      terms: theme.palette.secondary.main,
      other: theme.palette.grey[500]
    };
    return colors[category] || colors.other;
  };

  return (
    <Box>
      {/* Titre */}
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <CategoryIcon fontSize="small" />
          <span>
            {t('title')}
            {required && (
              <Typography component="span" color="error.main" sx={{ ml: 0.5 }}>
                *
              </Typography>
            )}
          </span>
        </Stack>
      </Typography>

      {/* Suggestions prédéfinies */}
      <FormControl fullWidth sx={{ mb: 2 }} variant={variant}>
        <InputLabel>{t('suggestedReason')}</InputLabel>
        <Select
          value={selectedReason || ''}
          onChange={(e) => {
            const newValue = e.target.value;
            
            // 1. Mettre à jour l'état local
            setSelectedReason(newValue);
            
            // 2. Informer le parent DIRECTEMENT - pas de useEffect
            if (onChange) {
              const finalReason = newValue === 'OTHER' ? customReason : (newValue ? t(`${context}.${newValue}`) : '');
              const isCurrentlyValid = Boolean(finalReason && finalReason.length > 0);
              
              onChange({
                reason: finalReason,
                analytics: { reason_key: newValue || 'CUSTOM', reason_text: finalReason },
                isValid: isCurrentlyValid
              });
            }
            
            if (onValidationChange) {
              const isCurrentlyValid = Boolean(newValue && newValue.length > 0);
              onValidationChange(isCurrentlyValid);
            }
          }}
          disabled={disabled}
          label={t('suggestedReason')}
        >
          <MenuItem value="">
            <em>{t('selectReason')}</em>
          </MenuItem>
          
          {/* Groupement par catégorie */}
          {Object.entries(reasonsByCategory).map(([category, reasons]) => [
            <ListSubheader key={`header-${category}`}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <CategoryIcon 
                  fontSize="small" 
                  sx={{ color: getCategoryColor(category) }}
                />
                <Typography variant="subtitle2">
                  {t(`categories.${category}`)}
                </Typography>
              </Stack>
            </ListSubheader>,
            
            ...reasons.map(({ key, label, category: reasonCategory }) => (
              <MenuItem key={key} value={key}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
                  <span style={{ flex: 1 }}>{label}</span>
                  <Chip 
                    label={t(`categories.${reasonCategory}`)} 
                    size="small" 
                    variant="outlined"
                    sx={{ 
                      fontSize: '0.7rem',
                      height: 20,
                      borderColor: getCategoryColor(reasonCategory),
                      color: getCategoryColor(reasonCategory)
                    }}
                  />
                </Stack>
              </MenuItem>
            ))
          ])}
        </Select>
      </FormControl>

      {/* Zone de texte personnalisée */}
      <TextField
        fullWidth
        multiline
        rows={4}
        variant={variant}
        label={
          selectedReason === 'OTHER' 
            ? t('customReason')
            : t('additionalDetails')
        }
        value={customReason}
        onChange={(e) => setCustomReason(e.target.value)}
        disabled={disabled}
        placeholder={
          selectedReason === 'OTHER'
            ? t('customPlaceholder')
            : t('detailsPlaceholder')
        }
        inputProps={{ maxLength: 500 }}
        error={errors.length > 0}
        helperText={
          errors.length > 0 
            ? errors[0]
            : `${customReason.length}/500 ${t('characters')}`
        }
        sx={{ mb: 2 }}
        InputProps={{
          endAdornment: isValid && customReason.length > 0 ? (
            <CheckIcon color="success" sx={{ mr: 1 }} />
          ) : undefined
        }}
      />

      {/* Erreurs de validation */}
      {errors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }} icon={<ErrorIcon />}>
          <Stack spacing={0.5}>
            {errors.map((error, index) => (
              <Typography key={index} variant="body2">
                {error}
              </Typography>
            ))}
          </Stack>
        </Alert>
      )}

      {/* Validation success */}
      {isValid && errors.length === 0 && getFinalReason().length > 0 && (
        <Alert severity="success" sx={{ mb: 2 }} icon={<CheckIcon />}>
          <Typography variant="body2">
            {t('validation.validationSuccess')}
          </Typography>
        </Alert>
      )}

      {/* Analytics preview (dev mode) */}
      {showAnalytics && isValid && (
        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="caption" component="pre" sx={{ fontSize: '0.7rem' }}>
            <strong>Analytics Data:</strong><br />
            {JSON.stringify(getAnalyticsData(), null, 2)}
          </Typography>
        </Alert>
      )}
    </Box>
  );
};

// Comparateur personnalisé pour éviter tout re-render inutile
const areEqual = (prevProps, nextProps) => {
  // Ne comparer que les props qui affectent vraiment le rendu
  if (prevProps.disabled !== nextProps.disabled) return false;
  if (prevProps.context !== nextProps.context) return false;
  
  // Ignorer userRole et autres props qui ne devraient pas affecter le rendu
  return true;
};

export default React.memo(CancellationReasonSelector, areEqual);
